import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { ManagementFormLayout } from 'components/ManagementFormLayout'
import { useUnsavedChangesGuard } from 'components/OrganizationScope/context'
import { TagChip } from 'components/TagChip'
import { TAG_NAME_MAX_LENGTH } from 'constants/index'
import { routes } from 'constants/routes'
import { useOrgPath } from 'hooks/useOrgPath'
import { commonStrings, tagManagementStrings } from 'constants/strings'
import { useTags, useUpdateTag } from 'hooks'
import type { TTagColor } from 'types'
import { TagColorPicker } from '../TagColorPicker'

const DEFAULT_COLOR: TTagColor = 'blue'

export default function TagManagementEdit() {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()
  const strings = tagManagementStrings.edit
  const { tagId } = useParams()
  const tagIdNumber = Number(tagId)
  const hasValidId = Number.isFinite(tagIdNumber) && tagIdNumber > 0

  // 単体取得APIは無いため、一覧から対象タグを引き当てる。
  const { tags, loading, error } = useTags()
  const tag = useMemo(
    () => tags.find((item) => item.id === tagIdNumber),
    [tags, tagIdNumber],
  )

  const [name, setName] = useState('')
  const [color, setColor] = useState<TTagColor>(DEFAULT_COLOR)
  const [validationError, setValidationError] = useState<string | undefined>()
  const markSaved = useUnsavedChangesGuard(JSON.stringify({ name, color }))

  const { update, isSubmitting, error: apiError } = useUpdateTag({
    onSuccess: () => {
      markSaved()
      navigate(orgPathFor(routes.TAG_MANAGEMENT), {
        state: { snackbarMessage: tagManagementStrings.messages.updateSuccess },
      })
    },
  })

  useEffect(() => {
    if (tag) {
      setName(tag.name)
      setColor(tag.color)
      setValidationError(undefined)
      markSaved()
      return
    }

    setName('')
    setColor(DEFAULT_COLOR)
    setValidationError(undefined)
    markSaved()
  }, [tag, markSaved])

  const isFormDisabled = loading || !tag
  const notFound = !loading && !error && (!hasValidId || !tag)
  const alertMessage =
    validationError ||
    apiError ||
    error ||
    (notFound ? strings.states.notFound : undefined)
  const trimmedName = name.trim()

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isFormDisabled || isSubmitting || !tag) return

    setValidationError(undefined)

    if (!trimmedName) {
      setValidationError(strings.errors.required)
      return
    }

    update(tag.id, { name: trimmedName, color })
  }

  return (
    <ManagementFormLayout
      kicker={strings.kicker}
      title={strings.title}
      description={strings.description}
      backTo={orgPathFor(routes.TAG_MANAGEMENT)}
    >
      {alertMessage && <Alert>{alertMessage}</Alert>}

      <form
        className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-1">
          <h2 className="text-sm font-semibold text-ink">
            {strings.form.title}
          </h2>
          <p className="text-xs text-muted">{strings.form.description}</p>
        </div>
        <label className="grid gap-2 text-xs font-semibold text-ink">
          {tagManagementStrings.form.fields.name.label}
          <Input
            name="name"
            placeholder={tagManagementStrings.form.fields.name.placeholder}
            autoComplete="off"
            required
            maxLength={TAG_NAME_MAX_LENGTH}
            disabled={isFormDisabled || isSubmitting}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <div className="grid gap-2 text-xs font-semibold text-ink">
          <span>{tagManagementStrings.form.colorLabel}</span>
          <TagColorPicker
            value={color}
            onChange={setColor}
            disabled={isFormDisabled || isSubmitting}
          />
        </div>
        {trimmedName && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted">
              {tagManagementStrings.form.preview}
            </span>
            <TagChip name={trimmedName} color={color} />
          </div>
        )}
        <div className="flex justify-end">
          <Button
            size="md"
            type="submit"
            disabled={isFormDisabled || isSubmitting}
          >
            {isSubmitting
              ? `${strings.actions.submit}${commonStrings.loadingEllipsis}`
              : strings.actions.submit}
          </Button>
        </div>
      </form>
    </ManagementFormLayout>
  )
}
